import { css } from 'styled-components'

import { app } from '../configuration'

// TYPES

export enum FontWeights {
  thin = 100,
  extraLight = 200,
  light = 300,
  regular = 400,
  medium = 500,
  semiBold = 600,
  bold = 700,
  extraBold = 800,
  black = 900,
}

export enum FontStyles {
  normal = 'normal',
  italic = 'italic',
  oblique = 'oblique',
}

type Font = {
  basename: string
  fontFamily: string
  fontWeight?: FontWeights
  fontStyle?: FontStyles
  fallback?: string
}

enum FontKeys {
  FiraSansExtraLight = 'FiraSansExtraLight',
  FiraSansRegular = 'FiraSansRegular',
  FiraSansMedium = 'FiraSansMedium',
  FiraSansSemibold = 'FiraSansSemibold',
  FiraSansBold = 'FiraSansBold',
  //
  MontserratExtraLight = 'MontserratExtraLight',
  MontserratRegular = 'MontserratRegular',
  MontserratMedium = 'MontserratMedium',
  MontserratSemibold = 'MontserratSemibold',
  MontserratBold = 'MontserratBold',
}

// DECLARATIONS

export const declarations: { [key in FontKeys]: Font } = {
  FiraSansExtraLight: {
    basename: 'FiraSans-ExtraLight',
    fontFamily: 'Fira Sans',
    fontWeight: FontWeights.extraLight,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  FiraSansRegular: {
    basename: 'FiraSans-Regular',
    fontFamily: 'Fira Sans',
    fontWeight: FontWeights.regular,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  FiraSansMedium: {
    basename: 'FiraSans-Medium',
    fontFamily: 'Fira Sans',
    fontWeight: FontWeights.medium,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  FiraSansSemibold: {
    basename: 'FiraSans-SemiBold',
    fontFamily: 'Fira Sans',
    fontWeight: FontWeights.semiBold,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  FiraSansBold: {
    basename: 'FiraSans-Bold',
    fontFamily: 'Fira Sans',
    fontWeight: FontWeights.bold,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  //
  MontserratExtraLight: {
    basename: 'Montserrat-ExtraLight',
    fontFamily: 'Montserrat',
    fontWeight: FontWeights.extraLight,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  MontserratRegular: {
    basename: 'Montserrat-Regular',
    fontFamily: 'Montserrat',
    fontWeight: FontWeights.regular,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  MontserratMedium: {
    basename: 'Montserrat-Medium',
    fontFamily: 'Montserrat',
    fontWeight: FontWeights.medium,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  MontserratSemibold: {
    basename: 'Montserrat-SemiBold',
    fontFamily: 'Montserrat',
    fontWeight: FontWeights.semiBold,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
  MontserratBold: {
    basename: 'Montserrat-Bold',
    fontFamily: 'Montserrat',
    fontWeight: FontWeights.bold,
    fontStyle: FontStyles.normal,
    fallback: 'sans-serif',
  },
}

// @FONT-FACE

export const fontsFaces: any[] = Object.values(declarations)?.map(
  (declaration) => css`
    @font-face {
      font-family: ${declaration.fontFamily};
      font-display: swap;
      font-weight: ${declaration.fontWeight || FontWeights.regular};
      font-style: ${declaration.fontStyle || FontStyles.normal};
      src: url('${app.PUBLIC_URL}/fonts/${declaration.basename}/${declaration.basename}.woff2')
        format('woff2');
    }
  `
)

// CSS

const fontCSS = (font: Font): string => `
    font-family: "${font.fontFamily}", ${font.fallback || 'sans-serif'};
    font-weight: ${font.fontWeight || FontWeights.regular};
    font-style: ${font.fontStyle || FontStyles.normal};
  `

const fonts: { [key in FontKeys]: string } = Object.keys(declarations)?.reduce(
  (acc, fontKey) => ({
    ...acc,
    [fontKey]: fontCSS(declarations[fontKey as FontKeys]),
  }),
  {} as any
)

export default fonts
